import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router';
import { api } from '../../api/client';
import { useAction } from '../../api/hooks';
import { useIsAdmin } from '../../api/session';
import { useCrumbs } from '../../layout/crumbs';
import { QueryView } from '../../ui/feedback';
import { usePageTitle } from '../../ui/misc';
import { ClaimDetails } from './implementation/ClaimDetails';
import { EvidenceCard } from './implementation/Evidence';
import { EvidenceDialog } from './implementation/EvidenceDialog';
import { ExceptionDialog } from './implementation/ExceptionDialog';
import { ImplementationHeader } from './implementation/Header';
import { Progress } from './implementation/Progress';
import { ReadinessChecklist } from './implementation/Readiness';
import { RecoverDialog } from './implementation/RecoverDialog';
import { ImplementationStatus } from './implementation/Status';
import type { ImplDetail } from './implementation/types';

/** WF-07–09, AI-04–06: shared implementation loading, routing and section composition. */
export function ImplementationPage() {
  const { key, number, implementationId } = useParams();
  const q = useQuery({
    queryKey: ['implementation', key, number, implementationId],
    queryFn: () =>
      api.get<ImplDetail>(`/projects/${key}/tickets/${number}/implementations/${implementationId}`),
  });
  usePageTitle(q.data ? `${q.data.ticket.key} · Implementation ${q.data.displayId}` : null);
  useCrumbs([
    { label: q.data?.project.name ?? key ?? '', to: `/projects/${key}` },
    { label: q.data?.ticket.key ?? `${key}-${number}`, to: `/projects/${key}/tickets/${number}` },
    { label: `Implementation ${implementationId}` },
  ]);
  const [dialog, setDialog] = useState<'recover' | 'evidence' | 'exception' | null>(null);
  const admin = useIsAdmin();
  const inv = [
    ['implementation', key, number],
    ['ticket', key, Number(number)],
    ['board', key],
  ];
  const ready = useAction(
    () =>
      api.post(`/projects/${key}/tickets/${number}/implementation/ready`, {
        expectedVersion: q.data!.ticket.version,
      }),
    { success: 'Moved to Pending release', invalidate: inv },
  );

  return (
    <div className="page">
      <QueryView query={q}>
        {(i) => (
          <>
            <ImplementationHeader
              i={i}
              projectKey={key}
              number={number}
              admin={admin}
              setDialog={setDialog}
              ready={ready}
            />
            <ImplementationStatus i={i} error={ready.error} />
            <div className="split">
              <div className="stack">
                <ReadinessChecklist i={i} />
                <EvidenceCard i={i} />
                <Progress i={i} />
              </div>
              <div className="stack">
                <ClaimDetails i={i} projectKey={key} />
              </div>
            </div>
            {dialog === 'recover' && <RecoverDialog i={i} onClose={() => setDialog(null)} inv={inv} />}
            {dialog === 'exception' && <ExceptionDialog i={i} onClose={() => setDialog(null)} inv={inv} />}
            {dialog === 'evidence' && <EvidenceDialog i={i} onClose={() => setDialog(null)} inv={inv} />}
          </>
        )}
      </QueryView>
    </div>
  );
}
